/**
 * DemoGate — Auto-login para URLs de demostración.
 *
 * Si la URL contiene "_demo" (ej: /campo/lote_s3_demo), el componente
 * llama a GET /api/auth/demo, setea la sesión y renderiza los children.
 * No hay Google OAuth ni OTP — es transparente para el visitante.
 *
 * Uso: wrapping de rutas en App.tsx o dentro de AppShell.
 */
import { useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';
import { useT } from '../../i18n';

const useStyles = makeStyles({
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100vh', gap: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

export const DEMO_TENANT_ID = 'demo';

/** Devuelve true si la URL actual es una ruta del tenant demo. */
export function isDemoPath(pathname: string): boolean {
  return pathname.startsWith(`/${DEMO_TENANT_ID}`);
}

interface DemoGateProps { children: ReactNode }

/**
 * Envuelve AppShell.  Si el path es demo y no hay sesión, auto-logea.
 * Si no es demo, se comporta de forma transparente.
 */
export function DemoGate({ children }: DemoGateProps) {
  const s = useStyles();
  const location = useLocation();
  const { token, setSession } = useAuthStore();
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const demo = isDemoPath(location.pathname);

  useEffect(() => {
    if (!demo || token) return;           // ya autenticado o no es demo
    setLoading(true);
    apiClient.get('/api/auth/demo')
      .then(res => {
        setSession(res.data.token, res.data.user);
      })
      .catch(() => setError(t.demoGate.error))
      .finally(() => setLoading(false));
  }, [demo, token, setSession, t]);

  if (demo && !token) {
    if (loading) {
      return (
        <div className={s.center}>
          <Spinner size="large" label={t.demoGate.loading} />
        </div>
      );
    }
    if (error) {
      return (
        <div className={s.center}>
          <Text size={400} style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Text>
        </div>
      );
    }
    return null; // esperando el useEffect
  }

  return <>{children}</>;
}
